<template>
  <transition name="sim-fade">
    <div v-if="active" class="sim-overlay">
      <div class="sim-card">
        <div class="sim-header">
          <span class="sim-title">Cierre de sesión por inactividad</span>
          <div class="sim-divider" />
        </div>
        <div class="sim-body">
          <div class="sim-countdown" :class="{ 'is-danger': isDangerTime }">
            <svg :width="circleSize" :height="circleSize" :viewBox="`0 0 ${circleSize} ${circleSize}`">
              <circle
                class="sim-track"
                :cx="circleSize / 2"
                :cy="circleSize / 2"
                :r="radius"
                fill="none"
                stroke-width="3"
              />
              <circle
                class="sim-progress"
                :cx="circleSize / 2"
                :cy="circleSize / 2"
                :r="radius"
                fill="none"
                stroke-width="5"
                stroke-linecap="round"
                :stroke-dasharray="circumference"
                :stroke-dashoffset="dashOffset"
                :transform="`rotate(-90 ${circleSize / 2} ${circleSize / 2})`"
              />
            </svg>
            <div class="sim-countdown-label">
              <span class="sim-number">{{ secondsLeft }}</span>
              <span class="sim-units">Segundos</span>
            </div>
          </div>
          <p class="sim-question">¿Sigues ahí?</p>
          <p class="sim-description">
            Tu sesión está a punto de expirar por inactividad.<br>
            Por motivos de seguridad, cerraremos tu sesión.
          </p>
        </div>
        <div class="sim-footer">
          <button class="sim-btn-cancel" @click="$emit('close-session')">Cerrar sesión</button>
          <button class="sim-btn-continue" @click="$emit('continue-session')">Continuar sesión</button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
const COUNTDOWN_SECONDS = 60
const DANGER_THRESHOLD_SECONDS = 9

export default {
  name: 'SessionInactivityModal',
  props: {
    active: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      secondsLeft: COUNTDOWN_SECONDS,
      intervalId: null,
      circleSize: 110
    }
  },
  computed: {
    radius () {
      return (this.circleSize - 5) / 2
    },
    circumference () {
      return 2 * Math.PI * this.radius
    },
    dashOffset () {
      return this.circumference * (1 - this.secondsLeft / COUNTDOWN_SECONDS)
    },
    isDangerTime () {
      return this.secondsLeft <= DANGER_THRESHOLD_SECONDS
    }
  },
  watch: {
    active: {
      immediate: true,
      handler (isActive) {
        isActive ? this.startCountdown() : this.stopCountdown()
      }
    }
  },
  beforeDestroy () {
    this.stopCountdown()
  },
  methods: {
    startCountdown () {
      this.stopCountdown()
      this.secondsLeft = COUNTDOWN_SECONDS
      this.intervalId = setInterval(() => {
        if (this.secondsLeft > 0) {
          this.secondsLeft--
        } else {
          this.stopCountdown()
          this.$emit('countdown-finished')
        }
      }, 1000)
    },
    stopCountdown () {
      if (this.intervalId) {
        clearInterval(this.intervalId)
        this.intervalId = null
      }
    }
  }
}
</script>

<style scoped>
.sim-overlay {
  position: fixed;
  inset: 0;
  background: rgba(40, 48, 86, 0.45);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.sim-card {
  width: 480px;
  max-width: calc(100vw - 40px);
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.18);
  overflow: hidden;
  font-family: 'Poppins', sans-serif;
}

.sim-header {
  padding: 22px 24px 0;
}

.sim-title {
  display: block;
  font-size: 16px;
  font-weight: 600;
  color: #283056;
  font-family: 'Poppins', sans-serif;
}

.sim-divider {
  height: 2px;
  background: #4c70ff;
  border-radius: 9px;
  margin-top: 14px;
}

.sim-body {
  padding: 24px 24px 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.sim-countdown {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}

.sim-track {
  stroke: #ECECEC;
}

.sim-progress {
  stroke: #4C70FF;
  transition: stroke-dashoffset 1s linear, stroke 0.3s ease;
}

.sim-countdown-label {
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.sim-number {
  color: #4C70FF;
  font-family: 'Poppins', sans-serif;
  font-size: 28px;
  font-weight: 700;
  line-height: 1;
  transition: color 0.3s ease;
}

.sim-units {
  color: #283056;
  font-family: 'Poppins', sans-serif;
  font-size: 11px;
  font-weight: 600;
}

.sim-countdown.is-danger .sim-progress { stroke: #FF756D; }
.sim-countdown.is-danger .sim-number   { color: #FF756D; }

.sim-question {
  color: #121212;
  text-align: center;
  font-family: 'Poppins', sans-serif;
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 12px;
}

.sim-description {
  color: #969696;
  text-align: center;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.6;
  margin: 0;
}

.sim-footer {
  padding: 20px 24px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.sim-btn-cancel {
  background: transparent;
  border: 1px solid #707070;
  border-radius: 5px;
  color: #707070;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 500;
  padding: 0 20px;
  min-height: 40px;
  cursor: pointer;
}

.sim-btn-continue {
  background: #5FD39C;
  border: none;
  border-radius: 5px;
  color: #ffffff;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 500;
  padding: 0 20px;
  min-height: 40px;
  cursor: pointer;
}

.sim-fade-enter-active,
.sim-fade-leave-active {
  transition: opacity 0.2s ease;
}
.sim-fade-enter,
.sim-fade-leave-to {
  opacity: 0;
}
</style>
